'use client';

import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

import { PaidAccessError } from '@/exceptions';
import { useGlobal } from '@/contexts/GlobalContext';
import { useToast } from '@/contexts/ToastContext';
import { useModal } from '@/contexts/ModalContext';
import { useDrawer } from '@/contexts/DrawerContext';
import HintModal from '@/components/goals/HintModal';
import LoadingModal from '@/components/common/LoadingModal';
import DrawerTitle from '@/components/common/DrawerTitle';
import GoalForm from '@/components/goals/GoalForm';
import { mutateWithoutLoading } from '@/hooks/swr/useSWRWithLoadingControl';
import { GoalCreateDataModel } from '@/types/goals';
import { createGoal } from '@/repositories/goals';

/**
 * ドロワー（目標新規作成）
 * @param param0
 * @returns
 */
const GoalDrawer: React.FC = () => {
  /**
   * React context
   */
  const context = useGlobal();
  const { showSuccessMessage, showWarningMessage, showErrorMessage } = useToast();
  const { openModal, closeModal } = useModal();
  const { closeDrawer } = useDrawer();

  /**
   * Next router
   */
  const navigate = useNavigate();
  const location = useLocation();

  /**
   * Goal新規作成
   */
  const handleFormSubmit = async (formData: GoalCreateDataModel) => {
    // ローディングモーダル表示
    openModal(<LoadingModal />);

    // 1秒の遅延を追加(更新中であることを見せる)
    await new Promise((resolve) => setTimeout(resolve, 500));

    try {
      // 目標新規作成
      const res = await createGoal(formData, context.csrftoken || '');
      if (!res.ok) {
        // NOTE: 課金機能アクセスエラーの場合
        if (res.code === PaidAccessError.CODE) {
          throw new PaidAccessError('課金機能にアクセスできません');
        } else {
          throw new Error(res.detail);
        }
      }

      // NOTE: mutateとnavigate(replace:true)の両方を実行し、情報を最新化
      mutateWithoutLoading();
      // NOTE:更新後、現在のルーティングを再更新（サーバーサイドで再フェッチ）する
      navigate(location.pathname, { replace: true, state: { skipLoading: true } });

      // トースト表示
      showSuccessMessage('新しいMy Goalsを設定しました。');

      // ドロワー閉じる
      closeDrawer();
    } catch (err) {
      if (err instanceof PaidAccessError) {
        showWarningMessage('更新できません。');
      } else {
        // エラートースト表示
        showErrorMessage('データ通信に失敗しました。しばらくしてから再度お試しください。');
      }
      throw err;
    } finally {
      // ローディングモーダル非表示
      closeModal();
    }
  };

  return (
    <>
      <div className="h-full flex flex-col w-full bg-white border-0 rounded-t-lg outline-none focus:outline-none">
        <div className="flex justify-between items-center border-b border-green-500 px-4 py-3 bg-white sticky top-0 rounded-t-lg">
          <div className="flex items-center">
            <DrawerTitle>新しい目標を設定</DrawerTitle>
          </div>
          <div className="flex space-x-2">
            <div
              className="flex items-center text-xs text-gray-500 ms-4 hover:text-blue-500 cursor-pointer"
              onClick={() => openModal(<HintModal />)}>
              <svg
                className="w-5 h-5"
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                fill="none"
                viewBox="0 0 24 24">
                <path
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M9.529 9.988a2.502 2.502 0 1 1 5 .191A2.441 2.441 0 0 1 12 12.582V14m-.01 3.008H12M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
                />
              </svg>
            </div>
          </div>
        </div>
        <GoalForm onFormSubmit={handleFormSubmit} />
      </div>
    </>
  );
};

export default GoalDrawer;
